/**
 * 佐賀県の背景ツアー「佐賀めぐり」コンテンツ。
 *
 * 軸は時間帯。早朝の呼子朝市から始めて、夜の嬉野温泉で終わる。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「日本三大朝市」（呼子）、「日本三大稲荷」（祐徳稲荷）、「日本三大美肌の湯」（嬉野）は
 *    いずれも公式で裏が取れない。使わない。
 *  - 祐徳稲荷の参拝者数は神社公式「300万人」と鹿島市公式「280万人」で食い違う。数字を出さない。
 *  - 武雄温泉楼門の建築年は武雄市「1915年」と文化遺産オンライン「1914年」で食い違う。
 *    豆知識では年を書かず、重要文化財の指定年（2005年7月22日）だけにしている。
 *  - 楼門の「釘が一本も使われていない」は武雄市公式にあるが国の資料にない。書かない。
 *  - 東よか干潟の「シギ・チドリ類の飛来数で全国1位」は環境省が明記しているので使える。
 *    ただし母集団（春期飛来数）と年（2020年）をセットで書く。
 *  - 有田焼の「日本初の磁器」「1616年」「李参平」は断定の仕方が難しいので使っていない。
 *  - 大魚神社の海中鳥居は「沖之神への参道」であって撮影用の構造物ではない。
 *    佐賀県遺産の登録名もそうなっている。はしゃぐ調子で書かない。
 */

import type { StageTour } from '../types';

export const sagaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'yobuko-asaichi',
      name: '呼子朝市',
      area: '唐津市呼子町・早朝',
      image: '/assets/scenery/saga/scenes/01_yobuko_asaichi.webp',
      hotspots: [
        {
          id: 'yobuko-tori',
          x: 0.5,
          y: 0.44,
          title: '二百メートルの通り',
          body: '朝市通りはおよそ200メートル。50軒ほどの露店が並び、朝7時30分から12時まで、1月1日を除いて毎日開かれています。大正時代から続くといわれる市で、値段のやりとりごと楽しむ場所です。',
          source: 'https://www.asobo-saga.jp/spots/detail/358d07ba-acf4-4be5-9573-9f73abc8dba1',
          mapQuery: '呼子朝市',
        },
        {
          id: 'yobuko-ika-shun',
          x: 0.22,
          y: 0.36,
          title: 'イカの旬はふた通り',
          body: '呼子でイカといっても一年中同じではありません。ケンサキイカは4月中旬ごろから10月中旬ごろ、アオリイカは10月中旬ごろから4月中旬ごろが旬です。季節によって皿にのるイカが入れ替わります。',
          source: 'https://www.asobo-saga.jp/articles/detail/b2d97eae-3915-4ae9-b04f-25f35eba3beb',
          relatedItemId: 'yobuko-ika',
          mapQuery: '呼子 イカ',
        },
      ],
    },
    {
      id: 'takeo-romon',
      name: '武雄温泉 楼門',
      area: '武雄市・朝',
      image: '/assets/scenery/saga/scenes/02_takeo_romon.webp',
      hotspots: [
        {
          id: 'takeo-tatsuno',
          x: 0.66,
          y: 0.3,
          title: '東京駅を建てた人の門',
          body: '武雄温泉の楼門は辰野金吾らの設計による木造二重門で、2005年7月22日に国の重要文化財に指定されました。伝統的な和風の意匠を基本にしながら、細部や骨組みに新しい試みが見られます。',
          source: 'https://online.bunka.go.jp/heritages/detail/202859',
          mapQuery: '武雄温泉 楼門',
        },
        {
          id: 'takeo-eto',
          x: 0.66,
          y: 0.17,
          title: '天井に四つだけの干支',
          body: '楼門2階の天井の四隅には、子・卯・馬・鳥の四つの干支が透かし彫りされています。残りの八つは、同じ辰野金吾がかかわった東京駅にあります。',
          source: 'https://www.city.takeo.lg.jp/take-oshi/tokubetsu/005782.html',
          mapQuery: '武雄温泉 楼門',
        },
        {
          id: 'takeo-kengaku',
          x: 0.56,
          y: 0.45,
          title: '見学会は朝の一時間',
          body: 'ガイドの案内つきの「楼門干支見学会」が開かれています。火曜を除く毎日の9時から10時まで（受付は9時30分まで）、所要20分ほど。期間は令和9年3月31日までです。',
          source: 'https://www.takeo-kk.net/sightseeing/001373.php',
          mapQuery: '武雄温泉 楼門',
        },
      ],
    },
    {
      id: 'higashiyoka-higata',
      name: '東よか干潟',
      area: '佐賀市東与賀町・午前の干潮',
      image: '/assets/scenery/saga/scenes/03_higashiyoka_higata.webp',
      hotspots: [
        {
          id: 'higata-ramsar',
          x: 0.55,
          y: 0.42,
          title: '2015年、条約の湿地に',
          body: '有明海のいちばん奥、佐賀市東与賀干拓の南に広がる泥干潟です。2015年5月にラムサール条約湿地に登録されました。面積は218ヘクタールあります。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/ramsarsitej/RamsarSites2025_Jp52.pdf',
          mapQuery: '東よか干潟',
        },
        {
          id: 'higata-shigi',
          x: 0.7,
          y: 0.34,
          title: '春の渡り鳥が全国一',
          body: 'シギ・チドリ類の春の飛来数で全国一位です。2020年の春は1万4763羽が記録されました。ズグロカモメやクロツラヘラサギなど絶滅のおそれのある鳥も立ち寄る、国内有数の中継地です。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/ramsarsitej/RamsarSites2025_Jp52.pdf',
          mapQuery: '東よか干潟',
        },
        {
          id: 'higata-shichimenso',
          x: 0.2,
          y: 0.5,
          title: '秋に赤くなる海辺の草',
          body: '秋になるとシチメンソウの群落が紅葉し、その後ろに干潟が広がります。干潟にはムツゴロウ、ワラスボ、シオマネキが暮らし、ムツかけやタカッポという伝統漁がいまも営まれています。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/ramsarsitej/RamsarSites2025_Jp52.pdf',
          relatedItemId: 'mutsugoro-kabayaki',
          mapQuery: 'シチメンソウ 東よか干潟',
        },
      ],
    },
    {
      id: 'yoshinogari',
      name: '吉野ヶ里歴史公園',
      area: '神埼市・昼',
      image: '/assets/scenery/saga/scenes/04_yoshinogari.webp',
      hotspots: [
        {
          id: 'yoshinogari-size',
          x: 0.55,
          y: 0.28,
          title: '四十ヘクタールのムラ',
          body: '南北およそ1キロメートル、東西およそ600メートル、面積およそ40ヘクタール。弥生時代における「国」の形成を考えるうえで欠かせない、大規模な環壕集落の跡です。',
          source: 'https://online.bunka.go.jp/heritages/detail/202936',
          mapQuery: '吉野ヶ里歴史公園',
        },
        {
          id: 'yoshinogari-shitei',
          x: 0.25,
          y: 0.3,
          title: '国と県でひとつの公園',
          body: '1992年に国営公園として整備することが決まり、2001年4月に一部が開園しました。いまは国営公園と県立公園を合わせて、およそ107ヘクタールが開いています。',
          source: 'https://www.yoshinogari.jp/information/overview/',
          mapQuery: '吉野ヶ里歴史公園',
        },
      ],
    },
    {
      id: 'okawachiyama',
      name: '秘窯の里 大川内山',
      area: '伊万里市・昼過ぎ',
      image: '/assets/scenery/saga/scenes/05_okawachiyama.webp',
      hotspots: [
        {
          id: 'okawachi-hanyo',
          x: 0.3,
          y: 0.13,
          title: '谷に隠した藩の窯',
          body: '江戸時代、佐賀藩は将軍家などへの献上品をつくっていました。1660年代、技法が外に漏れないよう、有田から険しい地形の大川内山へ藩窯を移します。入口には番所が置かれました。',
          source:
            'https://www.city.imari.lg.jp/secure/8666/%E6%97%85%E4%BC%8A%E4%B8%87%E9%87%8C01-06.pdf',
          mapQuery: '大川内山',
        },
        {
          id: 'okawachi-shiseki',
          x: 0.6,
          y: 0.28,
          title: '2003年、国の史跡に',
          body: '大川内鍋島窯跡は2003年9月16日に国の史跡に指定されました。御用窯跡は水平全長でおよそ137メートル、焼成室は27から30室と推定される階段状の登窯です。',
          source: 'https://www.city.imari.lg.jp/2983.htm',
          mapQuery: '大川内鍋島窯跡',
        },
        {
          id: 'okawachi-furin',
          x: 0.13,
          y: 0.35,
          title: '夏は谷じゅうが鳴る',
          body: '谷には30軒の窯元が集まっています。6月中旬から8月下旬の風鈴まつりの期間は、磁器でできた風鈴が軒先に下がります。陶石を砕く唐臼小屋も、水の力で音を立てて動いています。',
          source:
            'https://www.city.imari.lg.jp/secure/8666/%E6%97%85%E4%BC%8A%E4%B8%87%E9%87%8C01-06.pdf',
          mapQuery: '大川内山 風鈴まつり',
        },
      ],
    },
    {
      id: 'arita-uchiyama',
      name: '有田内山の町並み',
      area: '西松浦郡有田町・午後',
      image: '/assets/scenery/saga/scenes/06_arita_uchiyama.webp',
      hotspots: [
        {
          id: 'arita-denken',
          x: 0.5,
          y: 0.42,
          title: '十五・九ヘクタールの町並み',
          body: '1991年4月30日に国の重要伝統的建造物群保存地区に選ばれました。泉山の「上の番所」跡から岩谷川内の「下の番所」跡までの15.9ヘクタール。伝統的建造物165軒が守られています。',
          source: 'https://www.town.arita.lg.jp/kiji0031932/index.html',
          mapQuery: '有田内山 重要伝統的建造物群保存地区',
        },
        {
          id: 'arita-tonbai',
          x: 0.87,
          y: 0.45,
          title: '窯のれんがで積んだ塀',
          body: 'トンバイとは、登り窯を築くのに使った耐火れんがのことです。いらなくなったれんがと使い捨ての窯道具を赤土で塗り固めた塀が、泉山から美術館へ向かう裏通りに多く残っています。',
          source: 'https://www.asobo-saga.jp/spots/detail/e4bd49e7-fb68-46af-9d85-698091d20c95',
          mapQuery: '有田 トンバイ塀',
        },
      ],
    },
    {
      id: 'yutoku-inari',
      name: '祐徳稲荷神社',
      area: '鹿島市・午後',
      image: '/assets/scenery/saga/scenes/07_yutoku_inari.webp',
      hotspots: [
        {
          id: 'yutoku-1687',
          x: 0.4,
          y: 0.26,
          title: '一六八七年に建てられた',
          body: '貞享4年、西暦1687年に、鹿島藩主の夫人によって創建されました。祀られているのは倉稲魂大神、大宮売大神、猿田彦大神の三柱です。',
          source: 'https://www.yutokusan.jp/about/',
          mapQuery: '祐徳稲荷神社',
        },
        {
          id: 'yutoku-urushi',
          x: 0.3,
          y: 0.2,
          title: '総漆塗りの極彩色',
          body: '主要な建物は総漆塗りで、極彩色の華麗な楼門などが見事です。境内の命婦社は佐賀県の重要文化財に指定されています。',
          source: 'https://www.city.saga-kashima.lg.jp/main/323.html',
          mapQuery: '祐徳稲荷神社',
        },
      ],
    },
    {
      id: 'ouo-jinja',
      name: '大魚神社の海中鳥居',
      area: '藤津郡太良町・夕方の干潮',
      image: '/assets/scenery/saga/scenes/08_ouo_jinja.webp',
      hotspots: [
        {
          id: 'ouo-tsuki',
          x: 0.3,
          y: 0.45,
          title: '月の引力が見える町',
          body: '太良町のキャッチフレーズは「月の引力が見える町」。目に見えない月の引力を、有明海の大きな干満差を通して間接的に感じられる町です。町のほぼどこからでも有明海が望めます。',
          source: 'https://www.town.tara.lg.jp/kanko/_1725.html',
          mapQuery: '大魚神社 海中鳥居',
        },
        {
          id: 'ouo-30nen',
          x: 0.55,
          y: 0.17,
          title: '三十年に一基という習わし',
          body: '海浜に木の鳥居を30年に1基建てる習わしが伝えられています。佐賀県遺産には「沖之神への参道 大魚神社と海中鳥居」として登録されています。',
          source: 'https://www.pref.saga.lg.jp/dynamic/isan/info/pub/Detail.aspx?c_id=90&id=141',
          mapQuery: '大魚神社 海中鳥居',
        },
        {
          id: 'ouo-shio',
          x: 0.6,
          y: 0.33,
          title: '潮が引くと下を歩ける',
          body: '潮が引いたときには鳥居の全景が見え、下を歩いて通ることもできます。沖の島に置き去りにされた代官を大きな魚が背に乗せて帰したという言い伝えから、その魚を大魚大明神として祀ったものです。',
          source: 'https://www.pref.saga.lg.jp/dynamic/isan/info/pub/Detail.aspx?c_id=90&id=141',
          relatedItemId: 'takezaki-kani',
          mapQuery: '大魚神社 海中鳥居',
        },
      ],
    },
    {
      id: 'hamanoura-tanada',
      name: '浜野浦の棚田',
      area: '東松浦郡玄海町・日没',
      image: '/assets/scenery/saga/scenes/09_hamanoura_tanada.webp',
      hotspots: [
        {
          id: 'hamanoura-283',
          x: 0.45,
          y: 0.4,
          title: '二百八十三枚の田',
          body: '面積11.5ヘクタールのなかに、大小283枚の田んぼが連なります。水平線に沈む夕日が海面と水田を同じ色に染め、畦道が幾何学の模様を描きます。',
          source: 'https://www.town.genkai.lg.jp/site/kankou/1288.html',
          mapQuery: '浜野浦の棚田',
        },
        {
          id: 'hamanoura-erabareta',
          x: 0.28,
          y: 0.34,
          title: '二度えらばれた棚田',
          body: '1999年に「日本の棚田百選」に選ばれ、2022年2月14日には「つなぐ棚田遺産」にも認定されました。2020年には東京オリンピックの聖火リレーのコースにも選ばれています。',
          source: 'https://www.town.genkai.lg.jp/soshiki/19/73084.html',
          mapQuery: '浜野浦の棚田',
        },
      ],
    },
    {
      id: 'ureshino-onsen',
      name: '嬉野温泉',
      area: '嬉野市・夜',
      image: '/assets/scenery/saga/scenes/10_ureshino_onsen.webp',
      hotspots: [
        {
          id: 'ureshino-fudoki',
          x: 0.15,
          y: 0.28,
          title: '風土記に出てくる湯',
          body: '713年の肥前国風土記に「東の辺に湯の泉ありて能く人の病を癒す」と記されています。江戸時代には長崎街道の宿場町として栄えました。',
          source: 'https://www.asobo-saga.jp/spots/detail/c8f447ad-5b1a-4c3f-acd2-57bfa35f70fe',
          mapQuery: '嬉野温泉',
        },
        {
          id: 'ureshino-tofu',
          x: 0.28,
          y: 0.42,
          title: '豆腐がとろける湯',
          body: '泉質はナトリウム炭酸水素塩・塩化物泉で、源泉の温度は85度から90度。湯あがりには、嬉野温泉特産の、とろけるような温泉湯どうふがおすすめです。',
          source: 'https://www.asobo-saga.jp/spots/detail/c8f447ad-5b1a-4c3f-acd2-57bfa35f70fe',
          relatedItemId: 'ureshino-yudofu',
          mapQuery: '嬉野温泉 湯どうふ',
        },
        {
          id: 'ureshino-siebold',
          x: 0.72,
          y: 0.28,
          title: 'とんがり屋根の公衆浴場',
          body: '温泉街の公衆浴場「シーボルトの湯」は、ゴシック風の木造2階建て、およそ750平方メートル。オレンジ色のとんがり屋根が目印です。朝6時から夜10時まで開いています。',
          source: 'https://www.city.ureshino.lg.jp/kanko/siebold.html',
          mapQuery: 'シーボルトの湯',
        },
      ],
    },
  ],
};
